import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, styled } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FuncionarioAssinatura } from "../../../../domain/models/funcionario_assinatura";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { defaultFuncionarioAssinaturaForm, FuncionarioAssinaturaForm, FuncionarioAssinaturaSchema } from "../../../schemas/CadastroAssinaturas_lista/funcionario_assinatura";
import { FormSwitchInput } from "../../../../../../common/components/masked_inputs/react_forms/form_switch_input";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * CadastroAssinaturaModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedFuncionarioAssinatura: FuncionarioAssinatura | null,
  funcionariosDropdown: FuncionarioDropDown[],
  onSubmit: (form: FuncionarioAssinaturaForm) => void,
  onOpenChangeHistory: () => void,
  onDownload: (idFuncionarioAssinatura: number, arquivoNome: string) => void,
  onClose: () => void,
};

export const CadastroAssinaturaModal = ({
  open,
  saving,
  selectedFuncionarioAssinatura,
  funcionariosDropdown,
  onSubmit,
  onOpenChangeHistory,
  onDownload,
  onClose,
}: Props) => {
  const [funcionarioAssinatura, setFuncionarioAssinatura] = useState(selectedFuncionarioAssinatura);

  const hookForm = useForm<FuncionarioAssinaturaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: defaultFuncionarioAssinaturaForm,
    resolver: zodResolver(FuncionarioAssinaturaSchema),
  });

  const [limparArquivos] = hookForm.watch(['limparArquivos']);

  useEffect(() => {
    if (open) {
      if (selectedFuncionarioAssinatura) {
        hookForm.reset();
        hookForm.setValue('ativo', selectedFuncionarioAssinatura.ativo ? 1 : 0);
        hookForm.setValue('idFuncionario', selectedFuncionarioAssinatura.idFuncionario ?? 0);
        hookForm.setValue('nroRegistro', selectedFuncionarioAssinatura.nroRegistro ?? '');
      } else {
        hookForm.reset();
      }
      setFuncionarioAssinatura(selectedFuncionarioAssinatura ?? null);
    }
  }, [
    hookForm,
    open,
    selectedFuncionarioAssinatura,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  const handleDownload = useCallback(() => {
    if (!saving && funcionarioAssinatura) {
      onDownload(funcionarioAssinatura.idFuncionarioAssinatura, funcionarioAssinatura.nomeArquivo ?? '');
    }
  }, [
    saving,
    funcionarioAssinatura,
    onDownload,
  ]);

  const handleDeleteFile = useCallback(() => {
    if (!saving) {
      if (window.confirm('Você deseja excluir este item?')) {
        hookForm.setValue('limparArquivos', true);
        setFuncionarioAssinatura(null);
      }
    }
  }, [
    saving,
    hookForm,
  ]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving) {
      hookForm.setValue('limparArquivos', false);
      onChange(e.target.files?.item(0));
    }
  }, [
    saving,
    hookForm,
  ]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(onSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>
      <DialogTitle>
        Cadastrar Assinatura
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <FormSwitchInput
                name='ativo'
                readOnly={saving}
                reactHookForm={hookForm} />

              <DefaultFormAutocomplete
                name='idFuncionario'
                label='Funcionário '
                required={true}
                readOnly={saving}
                options={funcionariosDropdown}
                getOptionDisabled={x => !x.canBeSelected}
                getOptionLabel={(x) => x.nome}
                getOptionKey={x => x.idFuncionario}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='nroRegistro'
                label='Nº de Registro'
                required={false}
                readOnly={saving}
                reactHookForm={hookForm}
              />

              <div className="flex w-full">
                <Controller
                  name='file'
                  control={hookForm.control}
                  render={({ field }) => (funcionarioAssinatura && funcionarioAssinatura.nomeArquivo && funcionarioAssinatura.nomeArquivo.length > 0) ? (
                    <div className="w-full flex flex-row justify-between items-center">
                      <span className="cursor-pointer" onMouseDown={handleDownload}>
                        {funcionarioAssinatura.nomeArquivo && funcionarioAssinatura.nomeArquivo.length > 40
                          ? `${funcionarioAssinatura.nomeArquivo.substring(0, 40)}...`
                          : funcionarioAssinatura.nomeArquivo}
                      </span>
                      <IconButton onClick={handleDeleteFile}>
                        <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                      </IconButton>
                    </div>
                  ) : (
                    <PrimaryButton
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      disabled={saving}
                      startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                      {field.value && !limparArquivos ? field.value.name : "Escolher arquivo"}
                      <VisuallyHiddenInput
                        type="file"
                        onChange={(e) => handleFileChange(e, field.onChange)}
                        multiple={false} />
                    </PrimaryButton>
                  )} />
              </div>
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex px-4 pb-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedFuncionarioAssinatura) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={onOpenChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>

            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}